import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/utils/axios-utils";
import LoadingSpinner from "@/components/LoadingSpinner";
import SecondaryButton from "@/components/SecondaryButton";
import { mixpanelEvent, mixpanelEvents } from "@/utils/mixpanel-utils";
import StandardsHeatmapViewContent, {
  type StandardsDataState,
} from "./StandardsHeatmapViewContent";
import StandardsSummary from "../summary/StandardsSummary";
import { buildSummaryModel, summaryRootTarget } from "../summary/model";
import {
  readSummaryTarget,
  summarySearch,
  summaryTargetKey,
} from "../summary/navigation";
import type {
  SummaryContext,
  SummaryStudent,
  SummaryTarget,
} from "../summary/types";
import {
  demoStandardsFramework,
  isDemoAccount,
  loadDemoStandardsContext,
} from "../service/demoAnalytics";
import { STANDARDS_FRAMEWORKS } from "../constants/standardsFrameworkConfig";
import "../summary/standards-view.css";

const StandardsViewContent = () => {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const container = useRef<HTMLDivElement>(null);
  const initializedHistory = useRef(false);
  const positions = useRef({ summary: 0, heatmap: 0 });
  const [context, setContext] = useState<SummaryContext | null>(null);
  const [contextLoading, setContextLoading] = useState(true);
  const [contextError, setContextError] = useState(false);
  const [contextRetry, setContextRetry] = useState(0);
  const [mastery, setMastery] = useState<StandardsDataState>({
    students: [],
    loading: true,
    error: false,
    retry: () => {},
  });
  const framework = STANDARDS_FRAMEWORKS[demoStandardsFramework()];

  useEffect(() => {
    let cancelled = false;
    setContextLoading(true);
    setContextError(false);
    const live = api
      .get<SummaryContext>("/analytics/standards/context")
      .then(({ data }) => data);
    // Demo accounts swap in the captured roster — see service/demoAnalytics.
    (isDemoAccount("standards-context") ? loadDemoStandardsContext(live) : live)
      .then((data) => {
        if (!cancelled) setContext(data);
      })
      .catch(() => {
        if (!cancelled) setContextError(true);
      })
      .finally(() => {
        if (!cancelled) setContextLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contextRetry]);

  const available = context?.available === true;
  const explicitlySummary =
    params.get("standardsView") === "summary" || params.has("summaryLevel");
  const view =
    params.get("standardsView") === "heatmap" ||
    (!contextLoading && !contextError && !available && !explicitlySummary)
      ? "heatmap"
      : "summary";
  const root = useMemo(
    () =>
      context ? summaryRootTarget(context) : ({ level: "school" } as const),
    [context],
  );
  const target = useMemo(() => readSummaryTarget(params, root), [params, root]);
  const students = useMemo(
    () =>
      mastery.students.filter(
        (student): student is SummaryStudent => student.user_id !== undefined,
      ),
    [mastery.students],
  );
  const model = useMemo(
    () =>
      context && target && available
        ? buildSummaryModel(context, students, target, framework.map)
        : null,
    [context, students, target, available, framework],
  );
  const scrollElement = useCallback(() => {
    const main = container.current?.closest<HTMLElement>("#workspace-main");
    return main && main.scrollHeight > main.clientHeight
      ? main
      : (document.scrollingElement ?? document.documentElement);
  }, []);
  const setScroll = useCallback(
    (position: number) => {
      requestAnimationFrame(() => {
        scrollElement().scrollTop = position;
      });
    },
    [scrollElement],
  );
  const destination = useCallback(
    (next: SummaryTarget, nextView: "summary" | "heatmap" = "summary") => ({
      pathname: location.pathname,
      search: "?" + summarySearch(params, next, nextView).toString(),
    }),
    [location.pathname, params],
  );

  // Seed skipped ancestors so Back follows the breadcrumb hierarchy.
  useEffect(() => {
    if (initializedHistory.current || !available || !model || model.denied)
      return;
    initializedHistory.current = true;
    if (location.state?.standardsSummaryHistory === true) return;
    const chain = params.has("summaryLevel")
      ? model.breadcrumbs
      : [{ target: root }];
    chain.forEach(({ target: entry }, index) => {
      navigate(destination(entry, view), {
        replace: index === 0,
        state: { standardsSummaryHistory: true },
      });
    });
  }, [
    available,
    model,
    params,
    root,
    view,
    navigate,
    destination,
    location.state,
  ]);

  const previousTarget = useRef<string | null>(null);
  const targetKey = target ? summaryTargetKey(target) : "invalid";
  useEffect(() => {
    if (
      previousTarget.current !== null &&
      previousTarget.current !== targetKey
    ) {
      positions.current.summary = 0;
      if (view === "summary") setScroll(0);
    }
    previousTarget.current = targetKey;
  }, [targetKey, view, setScroll]);

  const openedScope = useRef<string | null>(null);
  useEffect(() => {
    if (
      view === "summary" &&
      context &&
      available &&
      model &&
      !model.denied &&
      !mastery.loading
    ) {
      const key = targetKey + ":" + context.viewer.role;
      if (openedScope.current !== key) {
        mixpanelEvent(mixpanelEvents.STANDARDS_SUMMARY_OPENED, {
          level: model.target.level,
          role: context.viewer.role,
        });
        openedScope.current = key;
      }
    } else if (view === "heatmap") openedScope.current = null;
  }, [view, available, model, context, targetKey, mastery.loading]);

  const onNavigate = (next: SummaryTarget) => {
    if (!context || !target) return;
    const nextModel = buildSummaryModel(context, students, next, framework.map);
    if (nextModel.denied) {
      navigate(destination(next));
      return;
    }
    const currentAncestors = model && !model.denied ? model.breadcrumbs : [];
    const ancestor = currentAncestors.findIndex(
      ({ target: entry }) => summaryTargetKey(entry) === summaryTargetKey(next),
    );
    if (
      ancestor >= 0 &&
      ancestor < currentAncestors.length - 1 &&
      initializedHistory.current
    ) {
      navigate(ancestor - currentAncestors.length + 1);
    } else {
      const from = nextModel.breadcrumbs.findIndex(
        ({ target: entry }) =>
          summaryTargetKey(entry) === summaryTargetKey(target),
      );
      const chain =
        from >= 0 ? nextModel.breadcrumbs.slice(from + 1) : [{ target: next }];
      chain.forEach(({ target: entry }) =>
        navigate(destination(entry), {
          state: { standardsSummaryHistory: true },
        }),
      );
    }
    positions.current.summary = 0;
    setScroll(0);
    mixpanelEvent(mixpanelEvents.STANDARDS_BREADCRUMB_CHANGED, {
      from_level: target.level,
      level: next.level,
      role: context.viewer.role,
    });
  };
  const switchView = (nextView: "summary" | "heatmap") => {
    positions.current[view] = scrollElement().scrollTop;
    navigate(destination(target ?? root, nextView), {
      replace: true,
      state: { standardsSummaryHistory: true },
    });
    setScroll(positions.current[nextView]);
    mixpanelEvent(mixpanelEvents.STANDARDS_VIEW_TOGGLED, {
      view: nextView,
      level: target?.level,
      role: context?.viewer.role,
    });
  };
  const toggle = (
    <div
      className="et-standards-view-toggle"
      role="group"
      aria-label="Standards view"
    >
      <button
        type="button"
        aria-pressed={view === "summary"}
        onClick={() => switchView("summary")}
        title="Summary shows mastery by school, teacher, grade, group or student."
      >
        Summary
      </button>
      <button
        type="button"
        aria-pressed={view === "heatmap"}
        onClick={() => switchView("heatmap")}
        title="Heatmap shows each student's mastery by standard."
      >
        Heatmap
      </button>
    </div>
  );
  const retry = () => {
    setContextRetry((value) => value + 1);
    mastery.retry();
  };
  const onEvent = (
    event: string,
    properties: Record<string, string | number>,
  ) => {
    const events: Record<string, mixpanelEvents> = {
      domain_opened: mixpanelEvents.STANDARDS_DOMAIN_OPENED,
      domain_grade_opened: mixpanelEvents.STANDARDS_DOMAIN_GRADE_OPENED,
      hover_opened: mixpanelEvents.STANDARDS_HOVER_OPENED,
    };
    if (events[event])
      mixpanelEvent(events[event], {
        ...properties,
        role: context?.viewer.role,
        level: target?.level,
      });
  };
  return (
    <div ref={container}>
      <div hidden={view !== "summary"}>
        {contextLoading || (available && mastery.loading) ? (
          <LoadingSpinner message="Loading standards summary..." />
        ) : contextError || mastery.error ? (
          <div role="alert" className="et-summary-empty">
            <p>Could not load standards summary.</p>
            <SecondaryButton size="sm" aria-label="Retry" onClick={retry}>
              Retry
            </SecondaryButton>
            <SecondaryButton
              size="sm"
              aria-label="Open Heatmap"
              onClick={() => switchView("heatmap")}
            >
              Open Heatmap
            </SecondaryButton>
          </div>
        ) : !available || !target || !model ? (
          <div className="et-summary-empty" role="alert">
            <p>You don&apos;t have access to this page.</p>
            <SecondaryButton
              size="sm"
              aria-label="Open Heatmap"
              onClick={() => switchView("heatmap")}
            >
              Open Heatmap
            </SecondaryButton>
          </div>
        ) : (
          <StandardsSummary
            context={context!}
            model={model}
            onNavigate={onNavigate}
            onEvent={onEvent}
            headerActions={toggle}
          />
        )}
      </div>
      <div hidden={view !== "heatmap"}>
        <StandardsHeatmapViewContent
          onStateChange={setMastery}
          headerActions={
            available || contextLoading || contextError ? toggle : undefined
          }
        />
      </div>
    </div>
  );
};

export default StandardsViewContent;
